import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sevkiyatService, cihazBilgiService, kullaniciService } from '../api/dbService';
import PageLayout from '../components/PageLayout';
import LoadingSpinner from '../components/LoadingSpinner';

function calculateBattery(pct) {
  if (!pct || pct <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

function BatteryBadge({ voltage }) {
  const pct = calculateBattery(voltage);
  const color = pct > 50 ? 'success' : pct > 20 ? 'warning' : 'danger';
  return (
    <div className="d-flex align-items-center gap-1" style={{ minWidth: 70 }}>
      <div className="progress flex-grow-1 battery-bar" style={{ height: 8 }}>
        <div className={`progress-bar bg-${color}`} style={{ width: `${pct}%` }} />
      </div>
      <small className="text-muted">{pct}%</small>
    </div>
  );
}

export default function Home() {
  const user = JSON.parse(localStorage.getItem('current_user') || '{}');
  const isAdmin = user.role === 'admin';

  const [sevkiyatlar, setSevkiyatlar] = useState([]);
  const [sensorData, setSensorData] = useState({});
  const [loading, setLoading] = useState(true);

  // Atama modal state
  const [ataModal, setAtaModal] = useState(null);
  const [tumKullanicilar, setTumKullanicilar] = useState([]);
  const [atananlar, setAtananlar] = useState([]);
  const [ataKullaniciId, setAtaKullaniciId] = useState('');
  const [ataLoading, setAtaLoading] = useState(false);
  const [backfillingId, setBackfillingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await sevkiyatService.getSevkiyatlar();
      const list = res.data || [];
      setSevkiyatlar(list);

      // Her sevkiyat için anlık sensör verisini çek
      const sensorMap = {};
      await Promise.allSettled(
        list.map(async s => {
          if (s.device_id) {
            try {
              const d = await cihazBilgiService.getSonVeri(s.device_id);
              sensorMap[s.device_id] = d.data;
            } catch {}
          }
        })
      );
      setSensorData(sensorMap);
    } catch (err) {
      toast.error('Sevkiyatlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, 30000);
    return () => clearInterval(timer);
  }, [fetchData]);

  useEffect(() => {
    if (!isAdmin) return;
    kullaniciService.getKullanicilar()
      .then(res => setTumKullanicilar(res.data || []))
      .catch(() => {});
  }, [isAdmin]);

  const openAtaModal = useCallback(async (sevkiyat) => {
    setAtaModal(sevkiyat);
    setAtaKullaniciId('');
    setAtaLoading(true);
    try {
      const res = await sevkiyatService.getSevkiyatKullanicilar(sevkiyat.id);
      setAtananlar(res.data || []);
    } catch { setAtananlar([]); }
    finally { setAtaLoading(false); }
  }, []);

  async function handleAta() {
    if (!ataKullaniciId) return toast.error('Kullanıcı seçin');
    try {
      await sevkiyatService.ataSevkiyatKullanici(ataModal.id, ataKullaniciId);
      const res = await sevkiyatService.getSevkiyatKullanicilar(ataModal.id);
      setAtananlar(res.data || []);
      setAtaKullaniciId('');
      toast.success('Kullanıcı atandı');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Atama başarısız');
    }
  }

  async function handleAtaKaldir(uid) {
    try {
      await sevkiyatService.sevkiyatKullaniciSil(ataModal.id, uid);
      const res = await sevkiyatService.getSevkiyatKullanicilar(ataModal.id);
      setAtananlar(res.data || []);
      toast.success('Atama kaldırıldı');
    } catch { toast.error('İşlem başarısız'); }
  }

  async function handleBackfill(id) {
    if (!window.confirm('Bu sevkiyatın cihazı için son 24 saatlik veri çekilsin mi?')) return;
    setBackfillingId(id);
    try {
      const res = await sevkiyatService.gecmisVeriCek(id, 24);
      toast.success(`${res.yazilan}/${res.cekilen} kayıt yazıldı`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Geçmiş veri çekilemedi');
    } finally {
      setBackfillingId(null);
    }
  }

  const atamaYapilmamislar = tumKullanicilar.filter(k => !atananlar.find(a => a.id === k.id));

  if (loading) return <PageLayout><LoadingSpinner /></PageLayout>;

  return (
    <PageLayout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0 fw-semibold">Aktif Sevkiyatlar</h5>
        <Link to="/add" className="btn btn-brand btn-sm">+ Yeni Sevkiyat</Link>
      </div>

      {sevkiyatlar.length === 0 ? (
        <div className="text-center text-muted py-5">
          <i className="fas fa-truck fa-3x mb-3 d-block opacity-25"></i>
          <p>Aktif sevkiyat bulunmuyor.</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle">
            <thead className="table-light">
              <tr>
                <th>Sevkiyat Kodu</th>
                <th>Cihaz Kodu</th>
                <th>Adı</th>
                <th>Güzergah</th>
                <th>Sıcaklık</th>
                <th>Pil</th>
                <th>Tarih</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sevkiyatlar.map(s => {
                const sensor = sensorData[s.device_id];
                const sicaklik = sensor?.sicaklik;
                const tempOk = sicaklik == null || (sicaklik >= (s.sicaklik_min ?? -99) && sicaklik <= (s.sicaklik_maks ?? 99));
                return (
                  <tr key={s.id}>
                    <td><small className="text-muted">{s.sevkiyat_no}</small></td>
                    <td><code>{s.device_id}</code></td>
                    <td>{s.sevkiyat_adi}</td>
                    <td>
                      <small>
                        {s.cikis_noktasi} <i className="fas fa-arrow-right text-muted mx-1"></i> {s.varis_noktasi}
                      </small>
                    </td>
                    <td>
                      {sicaklik != null ? (
                        <span className={tempOk ? 'temp-ok' : 'temp-warn'}>
                          {Number(sicaklik).toFixed(1)}°C
                        </span>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      {sensor?.voltaj ? <BatteryBadge voltage={sensor.voltaj} /> : <span className="text-muted">—</span>}
                    </td>
                    <td><small className="text-muted">{new Date(s.olusturma_tarihi).toLocaleDateString('tr-TR')}</small></td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap">
                        <Link to={`/view/${s.id}`} className="btn btn-outline-primary btn-sm">Detay</Link>
                        {isAdmin && (
                          <>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => openAtaModal(s)}>
                              Kullanıcı
                            </button>
                            <button className="btn btn-outline-info btn-sm"
                              disabled={backfillingId === s.id}
                              onClick={() => handleBackfill(s.id)}>
                              {backfillingId === s.id ? '...' : 'Geçmiş Çek'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Kullanıcı Atama Modal */}
      {ataModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 460 }}>
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h6 className="modal-title mb-0">Kullanıcı Ata</h6>
                  <small className="text-muted">{ataModal.sevkiyat_no} — {ataModal.sevkiyat_adi}</small>
                </div>
                <button className="btn-close" onClick={() => setAtaModal(null)} />
              </div>
              <div className="modal-body">
                {ataLoading ? <LoadingSpinner /> : <>
                  {atananlar.length > 0 && (
                    <div className="mb-3">
                      <small className="text-muted fw-semibold d-block mb-1">Atanmış Kullanıcılar</small>
                      <ul className="list-group list-group-flush">
                        {atananlar.map(k => (
                          <li key={k.id} className="list-group-item d-flex justify-content-between align-items-center px-0 py-1">
                            <span className="small">{k.username} <span className="text-muted">({k.email})</span></span>
                            <button className="btn btn-outline-danger btn-sm py-0 px-1"
                              onClick={() => handleAtaKaldir(k.id)}>✕</button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="d-flex gap-2">
                    <select className="form-select form-select-sm flex-grow-1"
                      value={ataKullaniciId} onChange={e => setAtaKullaniciId(e.target.value)}>
                      <option value="">— Kullanıcı seç —</option>
                      {atamaYapilmamislar.map(k => (
                        <option key={k.id} value={k.id}>{k.username} ({k.email})</option>
                      ))}
                    </select>
                    <button className="btn btn-brand btn-sm" onClick={handleAta}>Ata</button>
                  </div>
                </>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setAtaModal(null)}>Kapat</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
